import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import Icon from '@components/custom/Icon';
import ModuleHeader from '@components/ModuleHeader';
import ScrollList from '@components/custom/ScrollList';
import Spinner from '@components/custom/Spinner';
import Table from '@components/custom/Table';

import '@assets/css/finances.css';

import { crudActions } from '@sm/services/crudActions';
import { showAlert } from '@sm/store/alertSlice';
import { formatBalance } from '@sm/utils/utils';

const Balances = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const isLocal = location.pathname === '/local-balance';

  const [isLoading, setIsLoading] = useState(true);
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [currencies, setCurrencies] = useState([]);
  const [balances, setBalances] = useState({
    availableBalance: 0,
    availableBalanceByMethod: [],
    payoutBalance: 0,
    payoutBalanceByMethod: []
  });

  const methodBalanceColumns = useMemo(() => [{
    value: 'label',
    label: 'Method',
    leftAligned: true,
    isNumber: false
  }, {
    value: 'balance',
    label: 'Balance',
    rightAligned: true,
    isNumber: true
  }], []);

  useEffect(() => {
    setIsLoading(true);
    crudActions.get('v1/clients/lookups/balances')
      .then(response => {
        if (response) {
          const propertyName = isLocal ? 'localCurrencies' : 'globalCurrencies';
          setCurrencies(response[propertyName]);
          setSelectedCurrency(response[propertyName][0]?.value);
          setIsLoading(false);
        }
      })
      .catch(err => {
        dispatch(showAlert('danger', err?.message));
      });
  }, [dispatch, isLocal]);

  useEffect(() => {
    if (!selectedCurrency) return;
    
    setIsLoading(true);
    crudActions.get(`v1/merchant-balance/${selectedCurrency}?isGlobalCurrency=${!isLocal}`)
      .then(response => {
        setBalances({
          availableBalance: parseFloat(response?.availableBalance),
          availableBalanceByMethod: response?.availableBalanceByMethod,
          payoutBalance: parseFloat(response?.payoutBalance),
          payoutBalanceByMethod: response?.payoutBalanceByMethod
        });
        setIsLoading(false);
      })
      .catch(err => {
        dispatch(showAlert('danger', err?.message));
        setIsLoading(false);
      });
  }, [selectedCurrency, dispatch, isLocal]);

  const prepareTableData = (data) => {
    return data.map(elem => {
      return {
        ...elem,
        balance: formatBalance(elem.balance)
      };
    });
  };

  return (
    <div className='col-12 module finances'>
      <div className='component shadow mb-4 position-relative'>
        <ModuleHeader
          id='universal_currency_alt'
          title={ `${isLocal ? 'Local' : 'Global'} Currency Balance${isLocal ? 's' : ''}` }
          actions={
            <ScrollList
              items={ currencies }
              selectedItem={ selectedCurrency }
              onClick={ (value) => setSelectedCurrency(value) }
            />
          }
        />

        {isLoading && <Spinner /> }

        <div className='row info'>
          <div className={ `col-sm-12${isLocal ? ' col-md-6' : ''} item-wrap` }>
            <div className='item'>
              <div className='header'>
                <Icon name='finance' />
                Available Balance
              </div>
              <div className='body method-balance'>
                <div>
                  <span className='currency'>{ selectedCurrency }</span>
                  <span className='number'>{ formatBalance(balances.availableBalance) }</span>
                </div>
                {isLocal && balances.availableBalanceByMethod.length > 0 && (
                  <div className='balance-table'>
                    <Table
                      columns={ methodBalanceColumns }
                      data={ prepareTableData(balances.availableBalanceByMethod) }
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          {isLocal && (
            <div className='col-sm-12 col-md-6 item-wrap'>
              <div className='item'>
                <div className='header'>
                  <Icon name='equalizer' />
                Payout Balance
                </div>
                <div className='body method-balance'>
                  <div>
                    <span className='currency'>{ selectedCurrency }</span>
                    <span className='number'>{ formatBalance(balances.payoutBalance) }</span>
                  </div>
                  {isLocal && balances.payoutBalanceByMethod.length > 0 && (
                    <div className='balance-table'>
                      <Table
                        columns={ methodBalanceColumns }
                        data={ prepareTableData(balances.payoutBalanceByMethod) }
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Balances;